import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToId } from "@/data";

const ease = [0.16, 1, 0.3, 1];

export default function Statement({ eyebrow, lines, note, cta, dark = false, testid, videoUrl }) {
    const ink = dark ? "text-[#FAF8F5]" : "text-[#121211]";
    const mute = dark ? "text-white/70" : "text-[#121211]/70";
    return (
        <section
            className={`${dark ? "bg-[#121211]" : "bg-[#FBF9F5]"} px-6 py-32 lg:px-10 lg:py-48`}
            data-testid={testid}
        >
            <div className="relative mx-auto max-w-[1400px]">
                {videoUrl && (
                    <video
                        data-testid={`${testid}-video`}
                        className="statement-side-video"
                        src={videoUrl}
                        autoPlay
                        muted
                        loop
                        playsInline
                        aria-hidden="true"
                    />
                )}
                {eyebrow && (
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease }}
                        className={`mb-10 font-mono2 text-[11px] uppercase tracking-[0.34em] ${mute}`}
                    >
                        {eyebrow}
                    </motion.p>
                )}
                <motion.h2
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-60px" }}
                    className={`font-display text-[11vw] font-light leading-[0.98] tracking-tight sm:text-[7.6vw] lg:text-[5.6vw] ${ink}`}
                >
                    {lines.map((l, i) => (
                        <span key={l.text} className="block overflow-hidden pb-[0.14em]">
                            <motion.span
                                variants={{ hidden: { y: "110%" }, visible: { y: 0 } }}
                                transition={{ duration: 1.1, delay: i * 0.12, ease }}
                                className={`block ${l.italic ? "italic" : ""} ${l.indent ? "lg:pl-[12vw]" : ""}`}
                                data-testid={`${testid}-line-${i}`}
                            >
                                {l.text}
                            </motion.span>
                        </span>
                    ))}
                </motion.h2>
                {(note || cta) && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.9, delay: 0.3, ease }}
                        className="mt-12 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between lg:ml-[12vw] lg:mt-16"
                    >
                        {note && (
                            <p
                                className={`max-w-md text-lg font-light leading-relaxed lg:text-xl ${dark ? "text-white/85" : "text-[#121211]/85"}`}
                                data-testid={`${testid}-note`}
                            >
                                {note}
                            </p>
                        )}
                        {cta && (
                            <button
                                data-testid={`${testid}-cta`}
                                onClick={() => scrollToId("contact")}
                                className={`project-cta group inline-flex items-center gap-4 rounded-full px-8 py-4 font-mono2 text-xs uppercase tracking-[0.3em] ${dark ? "project-cta-light bg-[#FBF9F5] text-[#121211]" : "bg-[#121211] text-[#FBF9F5]"}`}
                            >
                                {cta}
                                <ArrowRight className="h-4 w-4 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-2" />
                            </button>
                        )}
                    </motion.div>
                )}
            </div>
        </section>
    );
}
